import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  animate,
  useAnimate,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import BackgroundImage from "../../../../components/ui/BackgroundImage";
import transparentBg from "../../assets/images/transparent-bg.webp";
import dollarImage from "../../../../features/gas-station/assets/images/dollar.png";
import PaymentButton from "./PaymentButton";
import useCart from "../../hooks/use-cart";
import CartProductsList from "./CartProductsList"; // ++ ADDED

interface Props {
  className?: string;
}

const SLAM = {
  type: "spring",
  stiffness: 850,
  damping: 18,
  mass: 0.7,
} as const;
const HAMMER = {
  type: "spring",
  stiffness: 600,
  damping: 22,  // Was 9 — low damping causes many oscillation frames burning CPU
  mass: 0.9,
} as const;

const OrderSummary = ({ className = "" }: Props) => {
  const [pulseKey, setPulseKey] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false); // ++ ADDED

  const isFirstRender = useRef(true);

  const [containerRef, containerAnimate] = useAnimate<HTMLDivElement>();
  const [badgeRef, badgeAnimate] = useAnimate<HTMLDivElement>();

  const count = useMotionValue(0);
  const displayPrice = useTransform(
    count,
    (v) => `$ ${new Intl.NumberFormat().format(Math.round(v))}`,
  );

  const { totalPrice, items } = useCart(); // ++ ADDED items

  const triggerPulse = () => {
    setPulseKey((k) => k + 1);
    badgeAnimate(
      badgeRef.current,
      { scale: [1, 1.2, 0.94, 1.1, 1] },
      { duration: 0.42, ease: "easeOut" },
    );
  };

  useEffect(() => {
    const shakeTimer = setTimeout(() => {
      containerAnimate(
        containerRef.current,
        { x: [-8, 8, -5, 5, -2, 2, 0] },
        { duration: 0.38, ease: "easeOut" },
      );
    }, 360);

    const countTimer = setTimeout(() => {
      animate(count, totalPrice, {
        duration: 1.25,
        ease: [0.16, 1, 0.3, 1],
        onComplete: triggerPulse,
      });
    }, 520);

    return () => {
      clearTimeout(shakeTimer);
      clearTimeout(countTimer);
    };
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    animate(count, totalPrice, {
      duration: 1.25,
      ease: [0.16, 1, 0.3, 1],
      onComplete: triggerPulse,
    });
  }, [totalPrice]);

  return (
    <>
      <motion.div
        ref={containerRef}
        initial={{ y: 90, opacity: 0, scale: 0.87 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ ...SLAM, delay: 0.04 }}
        // Removed rotateX + perspective — 3D transforms force a stacking context
        // and an extra compositor layer on every child, expensive in CEF.
      >
        <BackgroundImage
          imageSrc={transparentBg}
          imageClassName="!object-fill"
          className={`p-3 pr-0 ${className}`}
        >
          <div className="flex items-center space-x-2">
            <motion.div
              initial={{ scale: 0, rotate: -210, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ ...HAMMER, delay: 0.2 }}
            >
              <div
                className="relative p-1 rounded-sm w-[36px] h-[36px] bg-[#9EFF23]/10 flex items-center justify-center"
                style={{ boxShadow: "rgba(158,255,35,0.15) 0px 3px 16px 0px" }}
              >
                {/* Replaced infinite boxShadow animation with opacity on an overlay div.
                    boxShadow animation is CPU paint every frame; opacity is compositor-only. */}
                <motion.div
                  className="absolute inset-0 rounded-sm pointer-events-none"
                  style={{ boxShadow: "rgba(158,255,35,0.70) 0px 3px 40px 8px" }}
                  animate={{ opacity: [0.15, 1, 0.15] }}
                  transition={{
                    duration: 5.0,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.7,
                  }}
                />
                <motion.img
                  src={dollarImage}
                  alt="$"
                  className="w-[13px] h-[19px] object-contain relative z-10"
                  animate={{ y: [0, -5, 5, -3, 3, -1, 0] }}
                  transition={{ duration: 0.52, delay: 0.68, ease: "easeOut" }}
                />
              </div>
            </motion.div>

            <motion.div
              className="w-full"
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ ...SLAM, delay: 0.3 }}
            >
              <BackgroundImage
                imageSrc={transparentBg}
                imageClassName="!object-fill"
                className="p-2 py-1 w-full flex justify-between items-center"
              >
                <motion.div
                  className="flex flex-col"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ ...SLAM, delay: 0.42 }}
                >
                  <p className="text-white text-[0.95rem]">Total Price</p>

                  {/* ++ ADDED — "View cart" trigger */}
                  {/**      <motion.button
                    onClick={() => setIsCartOpen(true)}
                    className="flex items-center gap-1 text-[0.65rem] text-[#9EFF23]/50 hover:text-[#9EFF23]/90 transition-colors duration-200 group w-fit"
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.93 }}
                  >
                    <span className="group-hover:underline underline-offset-2">
                      View cart
                    </span>
                    <AnimatePresence mode="popLayout">
                      {items.length > 0 && (
                        <motion.span
                          key={items.length}
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.5, opacity: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 600,
                            damping: 20,
                          }}
                          className="bg-[#9EFF23]/15 text-[#9EFF23]/80 px-1 rounded-sm font-bold tabular-nums"
                        >
                          {items.length}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.button> */}
                </motion.div>

                <motion.div
                  ref={badgeRef}
                  className="relative bg-[#9EFF23]/10 py-1 px-3"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    scale: { ...HAMMER, delay: 0.56 },
                    opacity: { duration: 0.12, delay: 0.56 },
                  }}
                >
                  <motion.div
                    key={pulseKey}
                    className="absolute inset-0 pointer-events-none"
                    initial={{ boxShadow: "0 0 4px rgba(158,255,35,0.08)" }}
                    animate={
                      pulseKey > 0
                        ? {
                            boxShadow: [
                              "0 0  4px rgba(158,255,35,0.08)",
                              "0 0 32px rgba(158,255,35,0.90)",
                              "0 0 10px rgba(158,255,35,0.22)",
                              "0 0 32px rgba(158,255,35,0.90)",
                              "0 0 10px rgba(158,255,35,0.22)",
                              "0 0  4px rgba(158,255,35,0.08)",
                            ],
                          }
                        : { boxShadow: "0 0 0px rgba(158,255,35,0)" }
                    }
                    transition={{ duration: 1, ease: "easeInOut" }}
                  />

                  <AnimatePresence>
                    {pulseKey > 0 && (
                      <motion.div
                        key={pulseKey}
                        className="absolute inset-0 bg-[#9EFF23]/35 pointer-events-none"
                        initial={{ opacity: 1 }}
                        animate={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>

                  <motion.p className="relative z-10 text-center text-[#9EFF23] text-[1.050rem] font-bold tabular-nums">
                    {displayPrice}
                  </motion.p>
                </motion.div>
              </BackgroundImage>
            </motion.div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <PaymentButton type="card" />
            <PaymentButton type="cash" />
          </div>
        </BackgroundImage>
      </motion.div>

      {/* ++ ADDED — Cart popup */}
      {/**    <AnimatePresence>
        {isCartOpen && (
          <>
    
            <motion.div
              key="cart-backdrop"
              className="fixed inset-0 z-40 bg-black/60"
              style={{ backdropFilter: "blur(6px)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={() => setIsCartOpen(false)}
            />

      
            <motion.div
              key="cart-panel"
              className="fixed z-50 bottom-6 left-5  w-[420px] max-w-[calc(100vw-2rem)]"
              style={{ perspective: 900 }}
              initial={{ y: 64, opacity: 0, scale: 0.92, rotateX: 10 }}
              animate={{ y: 0, opacity: 1, scale: 1, rotateX: 0 }}
              exit={{ y: 48, opacity: 0, scale: 0.94, rotateX: 6 }}
              transition={{
                type: "spring",
                stiffness: 540,
                damping: 28,
                mass: 0.75,
              }}
            >
              <BackgroundImage
                imageSrc={transparentBg}
                imageClassName="!object-fill"
                className="p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <motion.p
                    className="text-white text-sm font-semibold tracking-wide"
                    initial={{ x: -14, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{
                      delay: 0.07,
                      type: "spring",
                      stiffness: 460,
                      damping: 24,
                    }}
                  >
                    Cart
                    {items.length > 0 && (
                      <span className="ml-2 text-[#9EFF23]/60 text-xs font-normal">
                        {items.length} {items.length === 1 ? "item" : "items"}
                      </span>
                    )}
                  </motion.p>

                  <motion.button
                    onClick={() => setIsCartOpen(false)}
                    className="text-white/40 hover:text-white/80 text-[0.7rem] transition-colors"
                    initial={{ x: 14, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{
                      delay: 0.09,
                      type: "spring",
                      stiffness: 460,
                      damping: 24,
                    }}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ✕ Close
                  </motion.button>
                </div>

                <CartProductsList />
              </BackgroundImage>
            </motion.div>
          </>
        )}
      </AnimatePresence> */}
    </>
  );
};

export default OrderSummary;
